import { BootstrapIcon, tryParseBootstrapIcon } from '@/components/webcomponents/Global/icons'
import { parseUrl } from '@/config/urls.js'
import {
  tryLoadDependencies,
  tryLoadLightDOMStyles,
  WebAwesomeComponent,
  registerBootstrapIconLibrary,
} from '@/components/webcomponents/Global/loadDependency'
import {
  NavEnterpriseMenuItemAttr,
  NavEnterpriseMenuItemClass,
  NavEnterpriseMenuItemDefaults,
  NavEnterpriseMenuItemSelector,
  NavEnterpriseMenuItemTarget,
  NavEnterpriseMenuItemValues,
  type NavEnterpriseMenuItemModel,
} from './types/NavEnterpriseMenuItem.type'

const IconAttributes = ['src', 'name', 'library', 'family', 'variant'] as const

class NavEnterpriseMenuItem extends HTMLElement {
  private linkElement: HTMLAnchorElement | null = null
  private iconElement: HTMLElement | null = null
  private labelElement: HTMLSpanElement | null = null
  private initialized = false

  private model: NavEnterpriseMenuItemModel = {
    href: NavEnterpriseMenuItemDefaults.href,
    label: NavEnterpriseMenuItemDefaults.label,
    iconName: NavEnterpriseMenuItemDefaults.iconName,
    iconLibrary: NavEnterpriseMenuItemDefaults.iconLibrary,
    iconFamily: NavEnterpriseMenuItemDefaults.iconFamily,
    iconVariant: NavEnterpriseMenuItemDefaults.iconVariant,
    iconSrc: NavEnterpriseMenuItemDefaults.iconSrc,
    active: NavEnterpriseMenuItemDefaults.active,
    target: NavEnterpriseMenuItemDefaults.target,
    ariaLabel: NavEnterpriseMenuItemDefaults.ariaLabel,
  }

  constructor() {
    super()
    tryLoadDependencies(document, [WebAwesomeComponent.Icon])
    tryLoadLightDOMStyles('NavEnterpriseMenuItem.css')
  }

  connectedCallback() {
    document.AscendAwesome?.LogManager?.logDebug('NavEnterpriseMenuItem connectedCallback')

    if (!this.initialized) {
      this.renderTemplate()
      this.captureDomReferences()
      // One-time read of HTML attributes so plain-HTML usage works (no observedAttributes needed)
      const href = this.getAttribute(NavEnterpriseMenuItemAttr.HREF)
      if (href) this.model.href = href
      const label = this.getAttribute(NavEnterpriseMenuItemAttr.LABEL)
      if (label !== null) this.model.label = label
      const iconName = this.getAttribute(NavEnterpriseMenuItemAttr.ICON_NAME)
      if (iconName) this.model.iconName = iconName
      this.model.iconLibrary =
        this.getAttribute(NavEnterpriseMenuItemAttr.ICON_LIBRARY) ?? this.model.iconLibrary
      this.model.iconFamily =
        this.getAttribute(NavEnterpriseMenuItemAttr.ICON_FAMILY) ?? this.model.iconFamily
      this.model.iconVariant =
        this.getAttribute(NavEnterpriseMenuItemAttr.ICON_VARIANT) ?? this.model.iconVariant
      this.model.iconSrc =
        this.getAttribute(NavEnterpriseMenuItemAttr.ICON_SRC) ?? this.model.iconSrc
      const active = this.getAttribute(NavEnterpriseMenuItemAttr.ACTIVE)
      if (active !== null) this.model.active = active !== 'false'
      const target = this.getAttribute(NavEnterpriseMenuItemAttr.TARGET)
      if (target) this.model.target = target
      const ariaLabel = this.getAttribute(NavEnterpriseMenuItemAttr.ARIA_LABEL)
      if (ariaLabel) this.model.ariaLabel = ariaLabel
      this.initialized = true
    }

    this.render()
  }

  get href() {
    return this.model.href
  }

  set href(value: string) {
    this.model.href = value?.trim() || NavEnterpriseMenuItemDefaults.href
    this.render()
  }

  get label() {
    return this.model.label
  }

  set label(value: string) {
    this.model.label = value?.trim() || ''
    this.render()
  }

  get icon() {
    return this.model.iconName
  }

  set icon(value: string) {
    this.model.iconName = value?.trim() || NavEnterpriseMenuItemDefaults.iconName
    this.render()
  }

  get iconLibrary() {
    return this.model.iconLibrary
  }

  set iconLibrary(value: string) {
    this.model.iconLibrary = value?.trim() || ''
    this.render()
  }

  get iconFamily() {
    return this.model.iconFamily
  }

  set iconFamily(value: string) {
    this.model.iconFamily = value?.trim() || ''
    this.render()
  }

  get iconVariant() {
    return this.model.iconVariant
  }

  set iconVariant(value: string) {
    this.model.iconVariant = value?.trim() || ''
    this.render()
  }

  get iconSrc() {
    return this.model.iconSrc
  }

  set iconSrc(value: string) {
    this.model.iconSrc = value?.trim() || ''
    this.render()
  }

  get active() {
    return this.model.active
  }

  set active(value: boolean) {
    this.model.active = Boolean(value)
    this.render()
  }

  get target() {
    return this.model.target
  }

  set target(value: string) {
    this.model.target = value?.trim() || NavEnterpriseMenuItemDefaults.target
    this.render()
  }

  get ariaLabel() {
    return this.model.ariaLabel
  }

  set ariaLabel(value: string) {
    this.model.ariaLabel = value?.trim() || NavEnterpriseMenuItemDefaults.ariaLabel
    this.render()
  }

  private renderTemplate() {
    this.innerHTML = `
      <a class="${NavEnterpriseMenuItemClass.MENU_ITEM}" href="${NavEnterpriseMenuItemDefaults.href}">
        <wa-icon aria-hidden="${NavEnterpriseMenuItemValues.ARIA_HIDDEN_TRUE}"></wa-icon>
        <span class="${NavEnterpriseMenuItemClass.LABEL}"></span>
      </a>
    `
  }

  private captureDomReferences() {
    this.linkElement = this.querySelector(NavEnterpriseMenuItemSelector.ANCHOR)
    this.iconElement = this.querySelector(NavEnterpriseMenuItemSelector.ICON)
    this.labelElement = this.querySelector(NavEnterpriseMenuItemSelector.LABEL)
  }

  // Item data can come from outside sources, so block script-capable URLs.
  private getSafeHref(href: string) {
    if (!href) return NavEnterpriseMenuItemDefaults.href
    try {
      const { protocol } = parseUrl(href, document.baseURI)
      return ['http:', 'https:', 'mailto:', 'tel:'].includes(protocol)
        ? href
        : NavEnterpriseMenuItemDefaults.href
    } catch {
      return NavEnterpriseMenuItemDefaults.href
    }
  }

  private getSafeIconSrc(src: string) {
    try {
      const url = parseUrl(src, document.baseURI)
      return url.protocol === 'https:' || url.origin === window.location.origin ? url.href : ''
    } catch {
      return ''
    }
  }

  private renderIcon(icon: HTMLElement) {
    IconAttributes.forEach((name) => icon.removeAttribute(name))
    const { iconFamily, iconVariant, iconSrc } = this.model
    let { iconName, iconLibrary } = this.model

    const src = iconSrc ? this.getSafeIconSrc(iconSrc) : ''
    if (src) {
      icon.setAttribute('src', src)
    } else {
      if (!iconLibrary) iconLibrary = 'bootstrap'

      if (iconLibrary === 'bootstrap') {
        if (!tryParseBootstrapIcon(iconName)) {
          console.error(
            `icon '${iconName}' not found in bootstrap library, using ${BootstrapIcon.Home}`,
          )
          iconName = BootstrapIcon.Home
        }
        if (!iconName) iconName = BootstrapIcon.Home
        registerBootstrapIconLibrary()
      }

      if (!iconName || !iconLibrary) {
        console.error('missing icon name or library', this.model)
        return
      }

      icon.setAttribute('name', iconName)
      icon.setAttribute('library', iconLibrary)
      if (iconFamily) icon.setAttribute('family', iconFamily)
      if (iconVariant) icon.setAttribute('variant', iconVariant)
    }
  }

  private render() {
    if (!this.initialized || !this.linkElement || !this.iconElement || !this.labelElement) return

    const label = this.model.label
    this.linkElement.href = this.getSafeHref(this.model.href)
    this.labelElement.textContent = label
    this.labelElement.title = label
    this.renderIcon(this.iconElement)

    const isActive = Boolean(this.model.active)
    this.linkElement.classList.toggle(NavEnterpriseMenuItemClass.ACTIVE, isActive)
    this.linkElement.setAttribute(
      NavEnterpriseMenuItemAttr.ARIA_CURRENT,
      isActive
        ? NavEnterpriseMenuItemValues.ARIA_CURRENT_PAGE
        : NavEnterpriseMenuItemValues.ARIA_CURRENT_FALSE,
    )

    const target = this.model.target || NavEnterpriseMenuItemDefaults.target
    if (target !== NavEnterpriseMenuItemTarget.SELF) {
      this.linkElement.setAttribute(NavEnterpriseMenuItemAttr.TARGET, target)
      if (target === NavEnterpriseMenuItemTarget.BLANK) {
        this.linkElement.setAttribute(
          NavEnterpriseMenuItemAttr.REL,
          NavEnterpriseMenuItemValues.REL_NOOPENER,
        )
      } else {
        this.linkElement.removeAttribute(NavEnterpriseMenuItemAttr.REL)
      }
    } else {
      this.linkElement.removeAttribute(NavEnterpriseMenuItemAttr.TARGET)
      this.linkElement.removeAttribute(NavEnterpriseMenuItemAttr.REL)
    }

    const ariaLabel = this.model.ariaLabel || ''
    if (ariaLabel) {
      this.linkElement.setAttribute(NavEnterpriseMenuItemAttr.ARIA_LABEL, ariaLabel)
    } else {
      this.linkElement.removeAttribute(NavEnterpriseMenuItemAttr.ARIA_LABEL)
    }
  }
}

if (!customElements.get('ts-nav-enterprise-menu-item')) {
  customElements.define('ts-nav-enterprise-menu-item', NavEnterpriseMenuItem)
}
export { NavEnterpriseMenuItem }
